// FILE: src/i18n/errors.js
// Server error code → words a student understands (plan §0 item 8, B-18,
// matrix 8.4 / 10.1). This is the only path from an error to the screen:
// pages call errorMessage(err) (or messageFor(code)) and never render
// err.message, a raw code, JSON or an enum. Unknown codes get FALLBACK.
const MESSAGES = {
  // Sign-in and identity (MTO §1)
  INVALID_CREDENTIALS: "That email and password don't match our records.",
  ACCOUNT_LOCKED:
    "Your account is locked after too many unsuccessful attempts. For your security, only student support can unlock it.",
  MAX_LOGIN_ATTEMPTS:
    "Your account is locked after too many unsuccessful attempts. For your security, only student support can unlock it.",
  MAX_2FA_ATTEMPTS:
    "Your account is locked after too many unsuccessful attempts. For your security, only student support can unlock it.",
  TWOFA_TOKEN_REQUIRED: "Enter the 6-digit code from your authenticator app.",
  INVALID_2FA_TOKEN: "That authenticator code isn't right. Check the app and try again.",
  INVALID_TOTP: "That authenticator code isn't right. Check the app and try again.",
  TOTP_RATE_LIMIT: "Too many tries in a short time. Wait a minute, then try again.",
  TWOFA_NOT_ENABLED: "The authenticator app isn't turned on for your account.",
  TWOFA_SETUP_NOT_STARTED: "Set up the authenticator app first.",
  INVALID_OTP: "That code isn't right. Check the email we sent and try again.",
  OTP_EXPIRED: "That code has expired. Start again to get a new one.",
  OTP_RESEND_TOO_SOON: "Please wait a few seconds before asking for another code.",
  OTP_REQUIRED: "Enter the code we emailed you.",
  PASSWORD_REQUIRED: "Enter your password to continue.",
  PASSWORD_EXPIRED:
    "Your password is more than 180 days old and must be changed. We've emailed you a link to set a new one.",
  PASSWORD_REUSED: "Choose a new password that's different from your current one.",
  WEAK_PASSWORD: "Your password doesn't meet all the rules below.",
  CHALLENGE_ANSWERS_REQUIRED: "Choose five different security questions and answer each one.",
  CHALLENGE_NOT_PENDING: "There's no identity check waiting. You can carry on.",
  COURSE_2FA_REQUIRED: "Please confirm it's you before you continue.",
  SECURITY_QUESTION_REQUIRED: "Type your answer to continue.",
  SECURITY_QUESTION_INVALID: "That answer doesn't match the one you gave when you registered.",
  SECURITY_QUESTION_NOT_FOUND: "Your account has no security questions yet. Contact student support.",
  NOT_ELIGIBLE: "The licence class must be G1, G2 or No licence yet.",
  INVALID_LICENSE: "Enter the licence number as it appears on the card (e.g. A1234-56789-01234).",
  INVALID_TOKEN: "This link is no longer valid. Ask for a new one.",
  EXPIRED_TOKEN: "This link has expired. Ask for a new one.",
  TOKEN_NOT_FOUND: "This link is incomplete. Open it again from your email.",
  INVITE_NOT_PENDING: "This invitation has already been used. Sign in instead.",
  ALREADY_REGISTERED: "Someone is already registered with this email address.",
  ALREADY_INVITED: "This email address already has an invitation waiting.",
  EMAIL_IN_USE: "That email address is already used by another account.",
  INVALID_EMAIL: "Enter a valid email address, like name@example.com.",
  EMAIL_REQUIRED: "Enter your email address.",
  USER_NOT_FOUND: "We couldn't find that account.",
  UNTIMED_NOT_ALLOWED: "Untimed accounts aren't allowed on this site.",

  // Sessions (MTO 1.4, 6.2)
  SESSION_IDLE: "You were signed out after 15 minutes without activity. Please sign in again.",
  SESSION_REPLACED: "You were signed out because your account signed in somewhere else.",
  INVALID_SESSION: "Your session has ended. Please sign in again.",
  LOGOUT_FAILED: "We couldn't sign you out cleanly. Close this browser window to be safe.",

  // Course time and enrolment (MTO 6.3, 17.x)
  COURSE_EXPIRED: "Your enrolment period has ended. Contact student support for help.",
  DAILY_LIMIT_REACHED:
    "You've reached today's limit of 5 hours of online lessons. You can continue tomorrow (Ontario time).",
  BREAK_REQUIRED: "You've been learning for 2 hours. Take a 15-minute break before you continue.",
  BREAK_NOT_REQUIRED: "You don't need a break right now.",
  COMPLETION_REJECTED_MIN_TIME:
    "This lesson isn't finished yet: some slides need more time before it can be marked complete.",
  LEARNING_SESSION_ENDED: "This lesson was opened somewhere else. Reload the page to continue here.",
  LEARNING_SESSION_NOT_FOUND: "This lesson session has ended. Reload the page to start again.",
  NOT_ENROLLED: "You're not enrolled in the course yet. Contact student support for help.",
  STUDENTS_ONLY: "This part of the site is for students.",
  RESOURCE_NOT_FOUND: "We couldn't find that lesson.",
  RESOURCE_LOCKED: "This lesson opens when you finish the one before it.",
  PROFILE_INCOMPLETE: "Finish your profile (address and consent) before starting lessons.",
  CONSENT_REQUIRED: "Please give your consent before starting lessons.",
  ONBOARDING_REQUIRED: "Finish the steps on the Getting started page before your first lesson.",
  ONBOARDING_ORDER: "Do the getting-started steps in order: tutorial, then the Code of Conduct, then the syllabus.",
  DOCUMENT_VERSION_MISMATCH: "This document has just been updated. Reload the page to read the new version.",
  DOCUMENT_NOT_FOUND: "We couldn't find that document.",
  HELP_ARTICLE_NOT_FOUND: "We couldn't find that help article. It may have moved: try the help centre search.",
  SURVEY_NOT_AVAILABLE: "The course survey opens when you've finished every chapter.",
  SURVEY_ALREADY_SUBMITTED: "You've already sent your course survey. Thank you!",

  // Forms and admin actions
  VALIDATION_FAILED: "Some details are missing or not in the right format.",
  MISSING_REQUIRED_FIELDS: "Fill in every required field.",
  FIELD_NOT_EDITABLE:
    "Your legal name and licence details can only be changed by student support, so we can keep your record accurate.",
  INVALID_EXTENSION: "Enter a number of days between 1 and 365, and a reason.",
  INVALID_CATEGORY: "Choose what kind of help you need.",
  INVALID_RATING: "Choose a rating from 1 to 5 for every question.",
  INVALID_STATUS: "Choose a valid status.",
  MESSAGE_REQUIRED: "Type a message before sending.",
  TICKET_NOT_FOUND: "We couldn't find that support request.",
  FEEDBACK_NOT_FOUND: "We couldn't find that survey response.",
  ACCOMMODATION_NOT_FOUND: "We couldn't find that accommodation request.",
  DDI_REGISTRATION_REQUIRED:
    "Only staff with a Digital Driving Instructor registration can answer course-content questions. Add the registration to your account, or ask a registered instructor.",
  ACCESS_LINK_INVALID: "This access link isn't valid. It may be mistyped or turned off: ask for a new one.",
  ACCESS_LINK_USED: "This access link has already been used. Sign in with your email and password instead.",
  INVITE_EMAIL_FAILED:
    "The account was created, but the invitation email couldn't be sent. Try sending it again from the student's page.",

  // Course content and curricula (WP-5; MTO Preface, CD2.4, 13.1)
  RESOURCE_IN_USE:
    "This lesson can't be deleted: students have progress on it, or another lesson belongs to it.",
  RESOURCE_ORDER_TAKEN:
    "Another lesson of the same type already has this position in this curriculum. Choose a different position.",
  CURRICULUM_NOT_FOUND: "We couldn't find that curriculum.",
  CURRICULUM_LOCKED:
    "This curriculum is approved, so its lessons can't be changed. Make a new version, change that, and have it approved again.",
  CURRICULUM_NOT_APPROVED: "Only an approved curriculum can be retired.",
  CURRICULUM_INVALID:
    "This curriculum doesn't pass the MTO checks yet, so it can't be approved. Open the check results to see what to fix.",
  CURRICULUM_LIMIT:
    "Three curricula are already approved, the most MTO allows. Retire one before approving another.",
  ADMIN_REQUIRED: "Only staff can do that.",
  // Staff roles and permissions (RBAC)
  PERMISSION_DENIED: "Your role doesn't allow this. Ask an administrator if you need it for your work.",
  ROLE_NOT_FOUND: "We couldn't find that role. It may have been deleted.",
  ROLE_IN_USE: "Staff still have this role. Give them another role first, then delete it.",
  ROLE_PROTECTED:
    "This is a built-in role: Administrator can't be changed at all, and the other built-in roles keep their name and can't be deleted.",
  LAST_ROLE_MANAGER:
    "That would leave nobody who can manage roles. Give another active staff member a role that can manage roles first.",
  CANNOT_CHANGE_OWN_ROLE: "You can't change your own role or account type. Ask another administrator.",
  ACCOUNT_ALREADY_LOCKED: "This account is already locked. Unlock it first if you need to lock it again with a new reason.",
  CANNOT_LOCK_SELF: "You can't lock your own account. Ask another administrator if it needs to be locked.",
  FORBIDDEN: "You don't have access to that.",
  NOT_FOUND: "We couldn't find what you were looking for.",
  NOT_IMPLEMENTED: "This part of the course isn't available yet.",
  CONTENT_NOT_FOUND: "Part of this lesson is missing. Contact technical support so we can fix it.",
  CONTENT_UNAVAILABLE: "This lesson couldn't load right now. Wait a moment, then try again.",
  INVALID_SCORE: "We couldn't record that quiz result. Finish the quiz again, or contact technical support.",
  RATE_LIMITED: "Too many requests in a short time. Wait a minute, then try again.",
  FILE_TOO_LARGE: "That file is too large. Choose one under 2 MB.",
  FILE_TYPE_NOT_ALLOWED: "Upload a photo (JPG or PNG) or a PDF.",
  INTERNAL_ERROR: "Something went wrong on our side. Please try again in a moment.",
  NETWORK: "We couldn't reach the server. Check your connection and try again."
};

const FALLBACK = "Something went wrong. Please try again, or contact student support.";

export function messageFor(code) {
  return MESSAGES[code] || FALLBACK;
}

const CODE_RE = /^[A-Z][A-Z0-9_]{2,}$/;
const NETWORK_RE = /failed to fetch|networkerror|network request failed|load failed|err_network/i;

/**
 * The server code inside anything a request can fail with: a code string,
 * a JSON body string (fetchWithAuth rejects with those), an Error with
 * .code/.body, a rejected thunk payload, or a fetch TypeError. Returns
 * null when there is no code (callers then show FALLBACK).
 */
export function errorCode(err) {
  if (!err) return null;
  if (typeof err === "string") {
    const s = err.trim();
    if (CODE_RE.test(s)) return s;
    if (s.startsWith("{")) {
      try {
        return errorCode(JSON.parse(s));
      } catch {
        return null;
      }
    }
    return NETWORK_RE.test(s) ? "NETWORK" : null;
  }
  if (typeof err === "object") {
    if (typeof err.code === "string" && CODE_RE.test(err.code)) return err.code;
    if (err.body && typeof err.body === "object") return errorCode(err.body);
    if (err.payload) return errorCode(err.payload);
    if (err.name === "TypeError" && NETWORK_RE.test(err.message || "")) return "NETWORK";
    if (typeof err.message === "string") return errorCode(err.message);
  }
  return null;
}

/** Plain-language message for any failure (see errorCode). */
export function errorMessage(err) {
  return messageFor(errorCode(err));
}

/** Extra numbers some failures carry, e.g. attemptsRemaining. */
export function errorBody(err) {
  if (!err) return {};
  if (typeof err === "object" && err.body && typeof err.body === "object") return err.body;
  if (typeof err === "object" && !(err instanceof Error)) return err;
  const raw = typeof err === "string" ? err : err.message;
  if (typeof raw === "string" && raw.trim().startsWith("{")) {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return {};
}

/** Reasons carried in /login?reason= after the server ended a session. */
export const REASON_CODES = {
  idle: "SESSION_IDLE",
  replaced: "SESSION_REPLACED",
  locked: "ACCOUNT_LOCKED",
  expired: "COURSE_EXPIRED",
  ended: "INVALID_SESSION"
};

export function reasonForCode(code) {
  const hit = Object.entries(REASON_CODES).find(([, c]) => c === code);
  return hit ? hit[0] : "ended";
}

export { FALLBACK };
export default MESSAGES;
